import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Menu, Modal } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Bike,
  BarChart3,
  ReceiptText,
  Users,
  Store,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Wallet,
  MessageSquare,
  Landmark,
} from "lucide-react";
import {
  PlusOutlined,
  OrderedListOutlined,
  FileTextOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../auth/AuthContext";
import { fetchChatUnreadCount } from "../../api/auth";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { VerificationLabel } from "../Page";

const { Sider } = Layout;

const NAV = [
  { key: "/", label: "Dashboard", icon: <LayoutDashboard className="h-4 w-4" aria-hidden /> },
  {
    key: "vehicles",
    label: "Vehicles",
    icon: <Bike className="h-4 w-4" aria-hidden />,
    children: [
      { key: "/vehicles/add", label: "Add Bike", icon: <Plus className="h-4 w-4" aria-hidden /> },
      { key: "/vehicles/all", label: "All Bikes", icon: <List className="h-4 w-4" aria-hidden /> },
      { key: "/vehicles/analytics", label: "Analytics", icon: <BarChart3 className="h-4 w-4" aria-hidden /> },
    ],
  },
  { key: "/sales", label: "Sale", icon: <ReceiptText className="h-4 w-4" aria-hidden />, children: [
      { key: "/sales/add", label: "Add Sale", icon: <PlusOutlined className="h-4 w-4" aria-hidden /> },
      { key: "/sales/manage", label: "Manage Sale", icon: <OrderedListOutlined className="h-4 w-4" aria-hidden /> },
      { key: "/sales/invoices", label: "Invoices", icon: <FileTextOutlined className="h-4 w-4" aria-hidden /> },
      { key: "/sales/invoice-settings", label: "Invoice Settings", icon: <FileImageOutlined className="h-4 w-4" aria-hidden /> },
    ]},
  { key: "/expenses", label: "Office Expenses", icon: <Wallet className="h-4 w-4" aria-hidden /> },
  { key: "/finance/ledger", label: "Finance & Accounts", icon: <Landmark className="h-4 w-4" aria-hidden /> },
  { key: "/messages", label: "Messages", icon: <MessageSquare className="h-4 w-4" aria-hidden /> },
  { key: "/members", label: "Team Members", icon: <Users className="h-4 w-4" aria-hidden /> },
  { key: "/branches", label: "Showrooms", icon: <Store className="h-4 w-4" aria-hidden /> },
];

function Plus({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function List({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
    </svg>
  );
}

/** Confirm before signing out — a stray click must not throw away an open tab. */
async function confirmSignOut(): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: "Sign out of BikerHat Partner?",
      content:
        "You'll be returned to the login screen. Any unsaved draft listing will stay on the server.",
      okText: "Sign out",
      cancelText: "Cancel",
      okType: "danger",
      centered: true,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

export function Sidebar() {
  const { seller, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);

  const isVehiclePage = useMemo(
    () => location.pathname === "/vehicles" || location.pathname.startsWith("/vehicles/"),
    [location.pathname],
  );

  // `openKeys` is left to the Menu (uncontrolled). A controlled `openKeys` with
  // no `onOpenChange` could never expand — which is exactly why the Vehicles
  // parent refused to open. `defaultOpenKeys` only seeds the *initial* state, so
  // a deep link onto a bike page arrives with the parent already open while a
  // plain click still toggles it.
  const defaultOpenKeys = useMemo(() => (isVehiclePage ? ["vehicles"] : []), [isVehiclePage]);

  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

  const { data: unreadData } = useQuery({
    queryKey: ["chat-unread-count"],
    queryFn: fetchChatUnreadCount,
    refetchInterval: 30000,
    enabled: !!seller,
  });
  const unreadCount = unreadData?.total || unreadData?.unreadCount || 0;

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key, { replace: true });
  };

  const handleCollapse = (newCollapsed: boolean) => {
    setCollapsed(newCollapsed);
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      collapsedWidth={72}
      width={280}
      breakpoint="lg"
      onBreakpoint={(broken) => {
        if (broken) setCollapsed(true);
      }}
      onCollapse={handleCollapse}
      className="bh-sider h-screen flex flex-col"
      trigger={null}
      style={{ 
        minHeight: '100vh',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Top: Brand + Collapse Toggle */}
      <div className="flex flex-col h-full overflow-hidden">
        <div className="bh-sider-brand flex-shrink-0 relative">
          <div className="bh-sider-mark">
            <span className="bh-sider-mark-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 5h6l4 4v8a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
                <path d="M17 5h4l4 4" />
              </svg>
            </span>
            {!collapsed && (
              <span className="bh-sider-mark-text flex items-center gap-2">
                <span className="bh-sider-brand-biker">BikerHat</span>
                <span className="bh-sider-brand-partner">Partner</span>
              </span>
            )}
          </div>
          
          {/* Collapse/Expand button at top right of brand area */}
          <button
            className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" aria-hidden />
            ) : (
              <ChevronLeft className="h-5 w-5" aria-hidden />
            )}
          </button>
        </div>

        {/* Navigation Menu - scrollable */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            defaultOpenKeys={defaultOpenKeys}
            inlineCollapsed={collapsed}
            onClick={handleMenuClick}
            className="bh-menu h-full overflow-y-auto scrollbar-hide"
            style={{ overflowY: 'auto' }}
          >
            {NAV.map((item) => {
              if (item.children) {
                return (
                  <Menu.SubMenu
                    key={item.key}
                    icon={item.icon}
                    title={
                      <span className={`text-sm font-medium ${collapsed ? "hidden" : "block"}`}>
                        {item.label}
                      </span>
                    }
                  >
                    {item.children.map((c) => (
                      <Menu.Item key={c.key} icon={c.icon}>
                        <span className={collapsed ? "hidden" : "block"}>{c.label}</span>
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                );
              }
              return (
                <Menu.Item key={item.key} icon={item.icon}>
                  <div className="flex items-center justify-between w-full">
                    <span className={collapsed ? "hidden" : "block"}>{item.label}</span>
                    {!collapsed && item.key === "/messages" && unreadCount > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </Menu.Item>
              );
            })}
          </Menu>
        </div>

        {/* Bottom: Account Info */}
        {seller && (
          <div className="bh-sider-account flex-shrink-0 pt-2">
            <div className="bh-sider-account-card">
              <Avatar className="bh-account-avatar">
                <AvatarFallback className="bg-primary text-white text-sm font-semibold">
                  {(seller.displayName?.split(/\s+/).map((w) => w[0]).join("")) || "?"}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="bh-account-info min-w-0">
                  <div className="bh-account-name truncate">{seller.displayName}</div>
                  {seller.email && <div className="bh-account-email truncate">{seller.email}</div>}
                </div>
              )}
            </div>
            
            {!collapsed && (
              <div className="mt-3">
                <VerificationLabel
                  level={seller.verificationLevel}
                  verifiedAt={seller.businessVerifiedAt ?? seller.identityVerifiedAt}
                  className="bh-account-verification"
                />
              </div>
            )}
            
            <div className="bh-sider-logout mt-3">
              <button
                className="bh-logout-btn w-full"
                onClick={(evt) => {
                  evt.stopPropagation();
                  void (async () => {
                    if (await confirmSignOut()) {
                      try {
                        await signOut();
                      } finally {
                        navigate("/login", { replace: true });
                      }
                    }
                  })();
                }}
              >
                <LogOut className={collapsed ? "mx-auto" : "mr-2 h-4 w-4"} aria-hidden />
                {!collapsed && <span>Sign out</span>}
              </button>
            </div>
          </div>
        )}
      </div>
    </Sider>
  );
}